// InvoiceModule — MIDNIGHT elevation
//
// Invoice section inside DetailDrawer.
// §10 glass nesting: ModulePanel = ds-tile-inset (flat rgba, no backdrop-filter).
// Header: SectionHeader in the invoice's phase tone (butter billed, mint paid,
// blush overdue); Send/Edit/Download via Button variant="icon".
// Status banner uses ds-tile-inset (no extra blur).
// All existing data logic (payments, balance, overdue) preserved unchanged.

import {
  Receipt, AlertCircle, Clock3, Check,
  CreditCard, Send, DollarSign,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/utils/formatting'
import type { Record_, Tone } from './types'
import { TONE_DOT_COLOR } from './_drawerHelpers'
import { SectionHeader, ModulePanel } from './_drawerHelpers'
import { InvoiceIconActions } from './DrawerHeaderActions'

interface InvoiceModuleProps {
  record: Record_
  onResendInvoice?: () => void
  onRecordPayment?: () => void
  onEditInvoice?: () => void
  onSendReceipt?: () => void
  /** False on a phone sheet: the same buttons render in the drawer header. */
  inlineActions?: boolean
}

export function InvoiceModule({
  record,
  onResendInvoice,
  onRecordPayment,
  onEditInvoice,
  onSendReceipt,
  inlineActions = true,
}: InvoiceModuleProps) {
  const isPaid   = record.stage === 'PAID'
  const overdue  = !isPaid && record.invoiceDueAt?.toLowerCase().includes('overdue')
  const accent: Tone = isPaid ? 'mint' : overdue ? 'blush' : 'butter'
  const totalPaid = (record.payments ?? []).reduce((s, p) => s + p.amount, 0)
  const balance   = isPaid ? 0 : (record.amountDue ?? record.total)

  const accentColor = TONE_DOT_COLOR[accent]

  return (
    <ModulePanel accent={accent}>
      {/* Header + action icon buttons */}
      <div className="ds-doc-head">
        <SectionHeader
          tone={accent}
          icon={Receipt}
          title={isPaid ? 'Receipt' : 'Invoice'}
          meta={
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {record.invoiceNumber}
            </span>
          }
        />
        {inlineActions && (
          <InvoiceIconActions
            record={record}
            onResendInvoice={onResendInvoice}
            onEditInvoice={onEditInvoice}
            onSendReceipt={onSendReceipt}
          />
        )}
      </div>

      {/* Status banner — ds-tile-inset (no blur) */}
      {!isPaid && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 12,
            // ds-tile-inset: flat, no backdrop-filter
            background: overdue ? 'rgb(var(--blush-rgb) / 0.10)' : 'rgb(var(--fg-rgb) / 0.04)',
            border: `0.5px solid ${overdue ? 'rgb(var(--blush-rgb) / 0.28)' : 'rgb(var(--fg-rgb) / 0.08)'}`,
            color: overdue ? 'var(--blush)' : 'rgb(var(--fg-rgb) / 0.60)',
          }}
        >
          {overdue ? (
            <AlertCircle style={{ width: 14, height: 14, flexShrink: 0 }} />
          ) : (
            <Clock3 style={{ width: 14, height: 14, flexShrink: 0 }} />
          )}
          <span>{record.invoiceDueAt}</span>
        </div>
      )}

      {isPaid && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgb(var(--mint-rgb) / 0.10)',
            border: '0.5px solid rgb(var(--mint-rgb) / 0.28)',
            color: 'var(--mint-text)',
          }}
        >
          <Check style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span>
            Paid in full{record.paidAt ? ` · ${record.paidAt}` : ''}
          </span>
        </div>
      )}

      {/* Totals */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            fontSize: 13,
          }}
        >
          <span style={{ color: 'var(--midnight-text3)' }}>Total</span>
          <span
            style={{
              color: 'rgb(var(--fg-rgb) / 0.60)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(record.total)}
          </span>
        </div>

        {totalPaid > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              fontSize: 13,
            }}
          >
            <span style={{ color: 'var(--midnight-text3)' }}>Paid</span>
            <span
              style={{ color: 'var(--mint-text)', fontVariantNumeric: 'tabular-nums' }}
            >
              −{formatCurrency(totalPaid)}
            </span>
          </div>
        )}

        {/* Balance */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            paddingTop: 8,
            marginTop: 4,
            borderTop: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
          }}
        >
          <span
            style={{ fontSize: 15, fontWeight: 600, color: 'rgb(var(--fg-rgb) / 0.80)' }}
          >
            Balance
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color:
                balance === 0
                  ? 'var(--mint)'
                  : overdue
                  ? 'var(--blush)'
                  : 'rgb(var(--fg-rgb))',
            }}
          >
            {formatCurrency(balance)}
          </span>
        </div>
      </div>

      {/* Payments list */}
      {record.payments && record.payments.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgb(var(--fg-rgb) / 0.30)',
              marginBottom: 8,
            }}
          >
            Payments
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {record.payments.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <CreditCard
                    style={{
                      width: 13,
                      height: 13,
                      color: 'var(--midnight-text3)',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        color: 'rgb(var(--fg-rgb) / 0.70)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.method}
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'var(--midnight-text3)',
                        fontVariantNumeric: 'tabular-nums',
                        marginTop: 1,
                      }}
                    >
                      {p.date}
                    </p>
                  </div>
                </div>
                <span
                  style={{ color: 'var(--mint-text)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                >
                  +{formatCurrency(p.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {(!isPaid || onEditInvoice || onSendReceipt) && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isPaid && balance > 0 && onRecordPayment && (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<DollarSign style={{ width: 14, height: 14 }} />}
              onClick={onRecordPayment}
              style={{ flex: 1 }}
            >
              Record payment
            </Button>
          )}
          {isPaid && onSendReceipt && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Send style={{ width: 14, height: 14 }} />}
              onClick={onSendReceipt}
              style={{ flex: 1 }}
            >
              Send receipt
            </Button>
          )}
        </div>
      )}
    </ModulePanel>
  )
}
